import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, CheckCircle2, Leaf } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/projects/${id}`);
        setProject(response.data);
      } catch (err) {
        setError('Project not found');
        console.error('Error fetching project:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="w-8 h-8 border-4 border-forest border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream px-6">
        <h1 className="font-serif text-3xl text-forest mb-4">Project Not Found</h1>
        <p className="text-foreground/70 mb-8">The project you're looking for doesn't exist.</p>
        <Link to="/projects">
          <Button className="btn-primary">
            <ArrowLeft className="mr-2 w-4 h-4" /> Back to Projects
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <main className="min-h-screen" data-testid="project-detail-page">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[600px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/50 to-transparent" />
        </div>
        
        <div className="relative z-10 w-full pb-16 px-6">
          <div className="container-custom">
            <Link 
              to="/projects"
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
              data-testid="back-to-projects"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Projects
            </Link>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block px-4 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm mb-4">
                {project.category}
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-4" data-testid="project-title">
                {project.title}
              </h1>
              <div className="flex items-center text-white/80">
                <MapPin className="w-5 h-5 mr-2" />
                <span>{project.location}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="section-padding bg-cream" data-testid="project-details">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Main Content */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="lg:col-span-2"
            >
              <h2 className="font-serif text-2xl md:text-3xl text-forest mb-6">Project Overview</h2>
              <p className="text-foreground/80 leading-relaxed text-lg mb-12">
                {project.description}
              </p>

              <h3 className="font-serif text-xl md:text-2xl text-forest mb-6">Design Features</h3>
              <ul className="space-y-4 mb-12">
                {project.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-forest flex-shrink-0 mt-0.5" />
                    <span className="text-foreground/80">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="bg-sand rounded-2xl p-8 sticky top-32">
                <h3 className="font-serif text-xl text-forest mb-6">Ecological Impact</h3>
                <div className="flex items-start gap-4 mb-8">
                  <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-6 h-6 text-forest" />
                  </div>
                  <p className="text-foreground/80">
                    {project.ecological_impact}
                  </p>
                </div>

                <div className="border-t border-border/50 pt-8">
                  <h4 className="font-medium text-forest mb-4">Project Location</h4>
                  <p className="text-foreground/70 mb-6">{project.location}</p>
                  
                  <h4 className="font-medium text-forest mb-4">Category</h4>
                  <span className="inline-block px-4 py-2 bg-forest/10 rounded-full text-forest text-sm">
                    {project.category}
                  </span>
                </div>

                <Link to="/contact" className="block mt-8">
                  <Button className="btn-primary w-full" data-testid="project-contact-btn">
                    Start Your Project
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-forest text-white">
        <div className="container-custom text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="font-serif text-3xl md:text-4xl mb-8">
              Interested in a Similar Project?
            </h2>
            <p className="text-white/80 max-w-xl mx-auto mb-8">
              Every landscape is unique. Let's discuss how we can design an ecological solution for your land.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-white text-forest hover:bg-white/90 rounded-full px-10 py-5 font-semibold text-sm tracking-wider uppercase"
                data-testid="project-detail-cta"
              >
                Get in Touch
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
